import React from 'react'
import { Box, Typography, Paper } from '@mui/material'
import {
  Reply as ReplyIcon,
  InsertDriveFile as DocumentIcon
} from '@mui/icons-material'
import MessageContent from './MessageContent'
import MessageStatusIcon from './MessageStatusIcon'
import {
  WHATSAPP_MESSAGE_USER_BG,
  WHATSAPP_MESSAGE_BOT_BG,
  WHATSAPP_TEXT_COLOR,
  WHATSAPP_SECONDARY_TEXT_COLOR
} from '../../constants/whatsapp'
import { Message } from './ConversationDetailDialog'
import { formatTime } from '../../utils/dateUtils'
import { QuoteBubble } from './QuoteBubble'

interface InteractiveMessageBubbleProps {
  message: Message
  allMessages?: any[]
  onScrollToQuote?: (id: string) => void
  leadName?: string
  smbName?: string
}





const InteractiveMessageBubble: React.FC<InteractiveMessageBubbleProps> = ({
  message,
  allMessages,
  onScrollToQuote,
  leadName,
  smbName
}) => {


  const isOutbound = message.direction === 'outbound'

  const interactiveMetadata = message.msg_interactive_metadata || {
    interactive_type: 'button',
    interactive_data: {}
  }

  const isReply = interactiveMetadata.interactive_type === 'button_reply'

  let interactiveData = interactiveMetadata.interactive_data || {}

  if (typeof interactiveData === 'string') {
    try {
      interactiveData = JSON.parse(interactiveData)
    } catch (e) {
      interactiveData = {}
    }
  }

  const data = interactiveData

  const header = data.header
  const footer = typeof data.footer === 'object' ? data.footer?.text : data.footer
  const bodyText = typeof data.body === 'object' ? data.body?.text : data.body
  const buttons = data.buttons || []

  const contextMessageId = message.msg_context_metadata?.context_message_id


  const renderHeader = () => {
    if (!header) return null

    switch (header.type) {
      case 'image':
        if (!header.link) return null;
        return (
          <Box sx={{ position: 'relative', width: '100%', maxHeight: 300, overflow: 'hidden', borderRadius: 0.5, mb: 1 }}>
            <img
              src={header.link}
              alt="Interactive header"
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        )
      case 'video':
        if (!header.link) return null;
        return (
          <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16/9', maxHeight: 300, overflow: 'hidden', borderRadius: 0.5, mb: 1 }}>
            <video
              src={header.link}
              controls
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          </Box>
        )
      case 'document':
      case 'doc':
        if (!header.link) return null;
        return (
          <Box
            sx={{
              width: '100%',
              p: 1.5,
              bgcolor: 'rgba(0,0,0,0.03)',
              borderRadius: 1,
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
            }}
            onClick={() => window.open(header.link, '_blank')}
          >
            <DocumentIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ color: WHATSAPP_TEXT_COLOR, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {header.filename || 'Document'}
            </Typography>
          </Box>
        )
      case 'text':
        if (!header.text) return null;
        return (
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 'bold',
              mb: 1,
              px: 1,
              pt: 0.5,
              color: WHATSAPP_TEXT_COLOR,
              wordBreak: 'break-word',
            }}
          >
            {header.text}
          </Typography>
        )
      default:
        return null
    }
  }

  return (
    <Paper
      elevation={1}
      sx={{
        bgcolor: isOutbound ? WHATSAPP_MESSAGE_USER_BG : WHATSAPP_MESSAGE_BOT_BG,
        color: WHATSAPP_TEXT_COLOR,
        borderRadius: 2,
        boxShadow: '0 1px 0.5px rgba(11,20,26,.13)',
        p: 0.5,
        position: 'relative',
        '& img': { pointerEvents: 'none' },
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, minWidth: 120 }}>
        {contextMessageId && (
          <QuoteBubble
            contextMessageId={contextMessageId}
            allMessages={allMessages}
            onScrollToQuote={onScrollToQuote}
            isOutbound={isOutbound}
            leadName={leadName}
            smbName={smbName}
          />
        )}
        {renderHeader()}

        <Box sx={{ px: 1, pb: 0.5, pt: 0.5 }}>
          {/* Body */}
          {(bodyText || message.content || isReply) && (
            <Typography
              component='div'
              variant='body2'
              sx={{
                color: WHATSAPP_TEXT_COLOR,
                fontSize: '14.2px',
                lineHeight: '19px',
                wordBreak: 'break-word',
                mb: footer ? 0.5 : 0
              }}
            >
              <MessageContent
                text={
                  (data.button_reply?.title || data.title) ||
                  bodyText ||
                  message.content ||
                  ''
                }
              />
            </Typography>
          )}

          {/* Footer and Timestamp */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mt: 0.5 }}>
            {footer && (
              <Typography variant="caption" sx={{ color: WHATSAPP_SECONDARY_TEXT_COLOR, fontSize: '12px', mr: 2 }}>
                {footer}
              </Typography>
            )}
            <Typography
              variant='caption'
              sx={{
                color: isOutbound ? WHATSAPP_SECONDARY_TEXT_COLOR : 'text.secondary',
                fontSize: '11px',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                ml: 'auto'
              }}
            >
              {formatTime(message.created_at)}
              {isOutbound && <MessageStatusIcon message={message} />}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Buttons */}
      {buttons.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1.5, borderTop: '1px solid rgba(0,0,0,0.05)', mx: -0.5, mb: -0.5 }}>
          {buttons.map((button: any, idx: number) => (
            <Box
              key={button.id || idx}
              sx={{
                borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.05)',
                py: 1.2,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.02)'
                }
              }}
            >
              <ReplyIcon sx={{ fontSize: 18, color: '#1b8755' }} />
              <Typography sx={{ color: '#1b8755', fontSize: '14px', fontWeight: 500 }}>
                {button.title}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Paper>
  )
}

export default InteractiveMessageBubble
